import { useRef, useState } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Positionsbezug, Vorgangsart } from "@kundeninfo/api-client";
// NUR der Typ — `vorgangsdetail` und `BelegParteien` zeigen typseitig aufeinander
// (der Block holt sich hier `KopffeldTabelle`). Beide Kanten sind `import type` und
// damit übersetzt weg; zur Laufzeit gibt es keinen Ring.
import type { BelegParteienEntwurf } from "./BelegParteien.js";
// Dieselbe Bauart: NUR der Typ, damit keine Laufzeit-Kante entsteht.
import type { ZahlplanEntwurf } from "./Zahlplan.js";

/**
 * Was ALLE Vorgangsdetails gemeinsam brauchen (Bereichs-Spalten, Etappe 1, Aufgabe 2,
 * Fixrunde 1).
 *
 * Angebot und Auftrag trugen dieselben sechs Helfer wörtlich zweimal — der Download-Anstoss,
 * die Gestalt der PDF-Rückmeldung, die beiden Feld→Wert-Umsetzer und der deutsche
 * Betragsformatierer. Beim RECHNUNGS-Detail wäre daraus die dritte Kopie geworden, und drei
 * Kopien laufen zuverlässig auseinander: Es reicht, dass jemand eine davon repariert.
 *
 * Was hier NICHT hingehört: alles, was einen Blocknamen kennt. Die Klassen bleiben je
 * Ansicht getrennt (`kang__`, `kauf__`, `krg__` — Kollisions-Lehre 01.09.). Die Gestalt der
 * Meldungen trägt seit ME-Etappe 3 `Hinweis` (`PDF_HINWEIS_ART`), keine Klasse mehr.
 */

/** Löst den blob-Download im Browser aus (Muster KundeDateien). In jsdom fehlt
 *  createObjectURL — dann wird der Anhang still übergangen. */
export function speichereBlob(blob: Blob, name: string): void {
  if (typeof URL.createObjectURL !== "function") return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Eine ApiError-Meldung herausziehen, sonst ein verständlicher Standardtext. */
export function apiMeldung(fehler: unknown, standard: string): string {
  if (fehler instanceof ApiError && fehler.message.trim() !== "") return fehler.message;
  return standard;
}

/**
 * Die Rückmeldung des „Als PDF"-Knopfes MIT ihrer Art (Muster KundeRechnungen,
 * MELDUNGS_STIL): `gut` ist der gelungene Download, `hinweis` die 501 der nicht
 * angebundenen Engine (bekannter Arbeitsstand, kein Fehlschlag — als Status angesagt,
 * neutral gezeichnet), `schlecht` jeder echte Fehlschlag (Fehlerfarbe, als Alarm
 * angesagt). Die Art wird MITGEFÜHRT, damit Klasse und Rolle nie auseinanderlaufen.
 */
export type PdfMeldung = {
  text: string;
  art: "gut" | "hinweis" | "schlecht";
  /**
   * Die FELDBEFUNDE, die ein Fehlschlag strukturiert mitbringt (`ApiError.fields`).
   *
   * Seit dem 05.09.2026 zählt der Meldungstext der API die Feldpfade der Engine NICHT mehr
   * auf — sie kommen ausschliesslich als `fields[]`. Wer sie nicht liest, zeigt sie
   * nirgends: Genau das war bis zur Fixrunde der Fall, und ein „… konnte nicht als PDF
   * ausgegeben werden." ohne die Liste sagt niemandem, WAS fehlt.
   */
  felder?: { field: string; message: string }[];
};

/**
 * Die Art des `Hinweis` (me-hinweis) je Meldungsart — seit ME-Etappe 3 (Aufgabe 4/5) für
 * Angebot, Auftrag und Rechnung (samt den Meldungsstreifen von Akten-Reiter und
 * Rechnungs-Bereich) statt eigener Klasse und Rolle (`pdfMeldungsStil`, entfallen): `ok`
 * sagt den Erfolg als Status an, `info` die 501 der nicht angebundenen Engine, `fehler`
 * einen echten Fehlschlag als Alarm (Rolle und Farbe bringt das Element mit).
 */
export const PDF_HINWEIS_ART: Record<PdfMeldung["art"], "ok" | "info" | "fehler"> = {
  gut: "ok", hinweis: "info", schlecht: "fehler",
};

/** Eine ganze Zahl aus einem Formularfeld — leer heisst `null` (nicht 0). „Keine
 *  Mindestlaufzeit" und „null Monate" sind zwei verschiedene Aussagen. */
export function ganzzahlOderNull(wert: string): number | null {
  const roh = wert.trim();
  if (roh === "") return null;
  const zahl = Number(roh);
  return Number.isFinite(zahl) ? zahl : null;
}

/** Ein leeres Textfeld ist `null`, kein leerer Text. */
export function oderNull(wert: string): string | null {
  return wert.trim() === "" ? null : wert;
}

/** Deutsche Kommazahl mitverstehen: „2.500,50" wie „2500.50". */
export function betragZahl(wert: string): number {
  const normalisiert = wert.trim().replace(/\./g, "").replace(",", ".");
  const zahl = Number(normalisiert);
  return Number.isFinite(zahl) ? zahl : Number.NaN;
}

/**
 * Die GEGENRICHTUNG zu `betragZahl` — und der Grund, warum es sie geben muss.
 *
 * Im Feld stand bis zur Fix-Runde 1 des Pakets 3b `String(a.netAmount)`, also „200.55".
 * `betragZahl` liest den Punkt jedoch als TAUSENDERTRENNZEICHEN (weil „2.500,50" der
 * deutsche Normalfall ist) und machte daraus 20055 — hundertfach zu viel. Mit Positionen
 * war das eine Sackgasse (422 `BETRAG_BERECHNET` an einem nur-lesenden Feld), ohne
 * Positionen ein still falsch gespeicherter Betrag. Wer eine Zahl in ein Feld schreibt, das
 * deutsch gelesen wird, muss sie deutsch schreiben.
 *
 * `useGrouping: false` lässt den Tausenderpunkt weg: Der Wert bleibt damit auch dann
 * eindeutig, wenn `betragZahl` eines Tages anders mit ihm umgeht.
 */
export const BETRAG_FELD = new Intl.NumberFormat("de-DE",
  { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });

export function betragText(wert: number): string {
  return Number.isFinite(wert) ? BETRAG_FELD.format(wert) : "";
}

/**
 * Die Beschriftung des Nettobetrags — und der Zusatz, der erklärt, warum er sich von selbst
 * ändert.
 *
 * Sie steht seit dem Paket „Positionen mit Präsenz" (08.09.2026) an ZWEI Stellen derselben
 * Maske: am Feld im eingefalteten Kopfblock und in dessen Kopfzeile, die den Betrag auch
 * eingeklappt zeigt. Zweimal abgeschrieben liefe der Zusatz eines Tages auseinander, und
 * dann behauptete die Kopfzeile einen Betrag von Hand, den das Feld darunter als berechnet
 * ausweist.
 */
export function nettobetragLabel(hatPositionen: boolean): string {
  return `Nettobetrag${hatPositionen ? " (berechnet aus Positionen)" : ""}`;
}

/**
 * Der Betrag für die Kopfzeile des eingefalteten Kopfblocks — WÖRTLICH der Text, der im
 * Feld darin steht.
 *
 * Bewusst nicht neu formatiert: Das Feld führt eine deutsch geschriebene Zeichenkette
 * (siehe `betragText`), und eine zweite Schreibweise in der Kopfzeile hiesse, dass
 * derselbe Betrag an zwei Stellen verschieden aussieht. Ein leeres Feld bekommt einen
 * Gedankenstrich: „0,00" wäre dort eine Behauptung über einen Betrag, den niemand erfasst
 * hat.
 */
export function kopfBetragText(feldwert: string): string {
  return feldwert.trim() === "" ? "—" : feldwert;
}

// ---------------------------------------------------------------------------
// Feldbefunde → das Feld im eingefalteten Kopfblock
// ---------------------------------------------------------------------------

/**
 * Die Abbildung SERVER-FELDNAME → Endung der Feld-Id im Formular, für die Felder, die im
 * eingefalteten Kopfblock stehen.
 *
 * **Warum es sie gibt.** Seit dem Paket „Positionen mit Präsenz" (08.09.2026) sind die
 * Kopffelder beim Bearbeiten zugeklappt. Ein Befund an einem von ihnen muss den Block
 * aufklappen, sonst steht die Meldung über einem Feld, das niemand sieht — die Fallenklasse
 * „unsichtbarer Fehler". Das gilt für BEIDE Quellen von Befunden, und die zweite ist beim
 * ersten Anlauf durchgerutscht:
 *
 *  - die Prüfung in der Maske (`pruefeAngebot`/`pruefeAuftrag`) und
 *  - die Prüfung auf dem SERVER, die als 422 mit `ApiError.fields[]` zurückkommt
 *    (`validateOffer`/`validateSalesOrder` in domain, dazu `BETRAG_BERECHNET` aus dem
 *    Positions-Repository und die `companyId`-Naht der Routen).
 *
 * Die Maskenprüfung nennt ihre Felder deshalb bei ihrem SERVERNAMEN, obwohl sie den Server
 * gar nicht fragt: Nur so gehen beide Wege durch dieselbe Tabelle, und ein künftiges Feld
 * kommt an EINER Stelle dazu statt an zweien — von denen die zweite niemandem auffiele, weil
 * ihr Fehlerfall ein unsichtbarer Fehler ist.
 *
 * Was NICHT in der Tabelle steht, ist absichtlich nicht darin: Notiz, Einleitungs- und
 * Ausleitungstext (und die Vorlagenwahl der Anlage) stehen AUSSERHALB des Kopfblocks und
 * sind immer zu sehen. Ein Befund an ihnen darf den Block nicht aufreissen — er würde etwas
 * öffnen, das mit der Meldung nichts zu tun hat.
 */
export type KopffeldTabelle = Readonly<Record<string, string>>;

/**
 * Serverfeldname → Kopffeld-Kennung, damit ein Feldfehler das richtige Feld im
 * eingeklappten Kopfblock aufklappt und fokussiert. Seit Migration 031 VIER Paare (Zahl
 * plus Einheit) statt der vorigen fünf Einzelfelder — an Angebot UND Auftrag dieselben,
 * weil der Server sie aus derselben Funktion prüft (`texteUndLaufzeitFehler` in
 * domain/salesRules.ts). Zweimal abgeschrieben liefe die eine Fassung beim nächsten Feld
 * der anderen davon.
 *
 * Zahl und Einheit eines Paares zeigen auf DIESELBE Feld-Id: Sie stehen visuell als EIN
 * Feldpaar nebeneinander (`kang__paar`/`kauf__paar`), und ein Befund an der Einheit soll
 * denselben Abschnitt öffnen und dorthin fokussieren wie einer an der Zahl — zwei
 * verschiedene Ziele für ein und dasselbe Paar wären eine Unterscheidung, die niemand sieht.
 */
export const LAUFZEIT_KOPFFELDER: KopffeldTabelle = {
  termValue: "term", termUnit: "term",
  minimumTermValue: "minterm", minimumTermUnit: "minterm",
  renewalPeriodValue: "renewal", renewalPeriodUnit: "renewal",
  noticePeriodValue: "notice", noticePeriodUnit: "notice",
  noticeReference: "notice",
};

/** Die volle Feld-Id im Formular mit dem Präfix dieser Maske — null, wenn das Feld nicht im
 *  Kopfblock steht. */
export function kopffeldId(
  tabelle: KopffeldTabelle, praefix: string, serverFeld: string,
): string | null {
  const endung = tabelle[serverFeld];
  return endung === undefined ? null : `${praefix}-${endung}`;
}

/**
 * Das ERSTE Feld eines Server-Befundes, das im Kopfblock steht (null = keines, dann bleibt
 * der Block zu).
 *
 * Das erste und nicht alle: Gezeigt werden kann nur eines, und die Meldung am Fuß nennt
 * ohnehin den Satz, den der Server zuerst gebildet hat (`DomainError` setzt seine Nachricht
 * aus `fields[0]`).
 *
 * Nimmt `unknown` und prüft selbst auf `ApiError`: Der Aufrufer steht in einem `catch`, und
 * dort ist alles möglich — ein Netzabbruch trägt keine Feldbefunde.
 */
export function kopffeldAusFehler(
  tabelle: KopffeldTabelle, praefix: string, fehler: unknown,
): string | null {
  if (!(fehler instanceof ApiError) || fehler.fields === undefined) return null;
  for (const befund of fehler.fields) {
    const id = kopffeldId(tabelle, praefix, befund.field);
    if (id !== null) return id;
  }
  return null;
}

/**
 * Ob zwei Entwürfe dieselben EINGABEN tragen — die Grundlage der Rückfrage vor dem
 * Abbrechen (Hausregel „striktes Modalpanel", 06.09.2026).
 *
 * Die Entwürfe der drei Vorgangsmasken sind flache Sätze aus einfachen Werten (siehe
 * `AngebotEntwurf` & Co. — Zeichenketten und der eine Schalter `openEnded`); verglichen
 * wird deshalb Feld für Feld über die Schlüssel des AUSGANGSSTANDES.
 *
 * Der Typ lässt bewusst nur einfache Werte zu: Ein Objekt darin verhielte sich nach seiner
 * Identität und meldete „geändert", sobald es neu gebaut wird.
 *
 * Ein handgeschriebener Vergleich je Maske wäre die Stelle, an der ein später hinzugefügtes
 * Feld stillschweigend aus der Rückfrage fiele — und mit ihm die Eingabe, die es hält.
 */
export function entwuerfeGleich<T extends Record<string, string | number | boolean | null>>(
  a: T, b: T,
): boolean {
  const schluessel = Object.keys(b) as (keyof T)[];
  return schluessel.every((k) => a[k] === b[k]);
}

/* ------------------------------------------------- Die Naht zum Kopfblock PARTEIEN */

/**
 * Der ZWEITE SPEICHERWEG eines Belegs — die Parteien (Adressen Etappe 3, Aufgabe 6).
 *
 * **Warum das hier steht und nicht dreimal in den Masken** (Ruling der Fixrunde 1): Es ist
 * derselbe Grund, aus dem es den Kopfblock nur einmal gibt. Angebot und Auftrag trugen
 * diese Funktion wörtlich gleich, die Rechnung denselben Weg inline — und schon in der
 * ersten Runde mit zwei Abweichungen (die Rechnung räumte den Entwurf auf den frischen
 * Stand, die anderen beiden nicht). Drei Fassungen einer Naht laufen genauso auseinander
 * wie drei Fassungen einer Maske.
 *
 * **Die Reihenfolge und die Version.** Der Kopf wird zuerst gespeichert; er trägt die
 * Pflichtfelder, und ein ungespeicherter Käufer ist das kleinere Übel als ein
 * gespeicherter Käufer an einem Kopf, den der Server abgewiesen hat. Die `version` ist
 * deshalb die aus der ANTWORT des Kopf-Speicherns: Beide Wege sperren über dieselbe
 * Version (die des Belegs, `belegKopf.version` in repositories/belegAdresse.ts), und das
 * Speichern des Kopfes hat sie gerade weitergedreht. Mit der gelesenen liefe der zweite
 * Weg zwingend in eine 409 über die eigene Änderung.
 *
 * **Unberührt wird nichts geschrieben** (`griff.ref.current === null`): Sonst schriebe
 * jedes Speichern des Kopfes auch die Parteien — und liefe an einem Beleg, an dem eine
 * gewählte Adresse inzwischen archiviert wurde, in eine 422, obwohl nur der Titel geändert
 * werden sollte.
 */
export async function speichereParteien(auftrag: {
  client: Pick<ApiClient, "setBelegParteien">;
  art: Vorgangsart;
  customerId: string;
  vorgangId: string;
  /** Die Version aus der Antwort des Kopf-Speicherns. */
  version: number;
  griff: ParteienGriff;
  /**
   * Felder, die die MASKE selbst führt und die deshalb ihren Wert gewinnen.
   *
   * Genau einer benutzt das: der AUFTRAG mit seiner `customerPurchaseOrderNumber`. Der
   * Entwurf des Blocks trägt die Bestellnummer aus dem LADEN mit sich (der Block zeigt sie
   * dort nicht), und `setBelegParteien` verlangt das Feld im Rumpf — ohne diese
   * Übersteuerung überschriebe der zweite Weg still die Nummer, die der Kopf eine Zeile
   * vorher gespeichert und durchgeschrieben hat.
   */
  ueberschreibt?: Partial<BelegParteienEntwurf>;
  /** Der Kopf steht, die Parteien nicht: Die Maske schreibt den frischen Vorgang fort,
   *  zeigt die Meldung am Fuss und klappt auf das bemängelte Feld auf. */
  beiFehler: (meldung: string, fehler: unknown) => void;
  /**
   * Die Version, die der Beleg NACH diesem zweiten Weg trägt — er dreht sie ein zweites
   * Mal weiter (`version = version + 1` in repositories/belegAdresse.ts).
   *
   * Wer nach dem Speichern NEU LÄDT (Angebot und Auftrag schliessen ihren Eintrag und
   * laden die Liste), braucht sie nicht. Wer den Eintrag OFFEN lässt, braucht sie
   * zwingend: Die RECHNUNG tut das (hinter dem Panel steht ihr Lese-Detail mit
   * „Festschreiben"), und mit der Version aus dem Kopf-Speichern liefe die nächste
   * Handlung an diesem Beleg in eine 409 über die eigene Änderung.
   */
  beiErfolg?: (version: number) => void;
}): Promise<boolean> {
  const stand = auftrag.griff.ref.current;
  if (stand === null) return true;
  try {
    const antwort = await auftrag.client.setBelegParteien(
      auftrag.art, auftrag.customerId, auftrag.vorgangId,
      { version: auftrag.version, ...stand, ...auftrag.ueberschreibt });
    auftrag.griff.zuruecksetzen();
    auftrag.beiErfolg?.(antwort.version);
    return true;
  } catch (roh) {
    const fehler = bestellnummerBefund(roh);
    auftrag.griff.setzeBefunde(fehler);
    auftrag.beiFehler(
      apiMeldung(fehler, "Die Parteien konnten nicht gespeichert werden."), fehler);
    return false;
  }
}

/**
 * Die Bestellnummer prüft der Server als ADRESSE (Art „Bestellnummer", `validateAddress`):
 * Ein Befund an ihr kommt deshalb mit `field: "value"` — dem Feldnamen der Adresse, nicht
 * dem des Parteien-Rumpfes. Ohne Umschreiben stünde er an keinem Feld (Kopfblock zählt
 * keinen Fehler, am Fuss nur „Höchstens 35 Zeichen sind zulässig." ohne Bezug). Seit
 * ME-Etappe 3 (Aufgabe 5) ist die Grenze am Feld weich (`max`, kein `maxLength`), der Weg
 * also erreichbar (Review A5, W1). Im Parteien-Weg ist `value` immer die Bestellnummer —
 * die übrigen Plätze sind Wahlen (Kennungen), keine getippten Werte.
 */
function bestellnummerBefund(fehler: unknown): unknown {
  if (!(fehler instanceof ApiError) || fehler.fields === undefined
      || !fehler.fields.some((f) => f.field === "value")) {
    return fehler;
  }
  return new ApiError(fehler.status, fehler.code, fehler.message,
    fehler.fields.map((f) => (f.field === "value" ? { ...f, field: "purchaseOrderNumber" } : f)),
    fehler.incidentId, fehler, fehler.conflict, fehler.locked);
}

/**
 * Der Griff der Maske auf den Entwurf ihres Kopfblocks PARTEIEN.
 *
 * **Eine REF und ein Schalter, kein Zustand für den Entwurf selbst.** Der Block zeichnet
 * sich selbst; die Maske braucht seinen Stand nur im Augenblick des Speicherns. Läge er im
 * Zustand, zeichnete jedes getippte Zeichen der Bestellnummer die ganze Maske neu. Der
 * SCHALTER `beruehrt` liegt trotzdem im Zustand — der Abbrechen-Wächter und
 * `useBeforeUnload` müssen davon erfahren, und eine Ref erfährt niemand (React zeichnet
 * nach ihrer Änderung nicht neu). Er kippt genau einmal; jedes weitere `true` lässt React
 * ohne neues Bild liegen.
 *
 * `befunde` hält die Feldbefunde des Servers (Servername → Satz) für die Anzeige AM FELD
 * (`aria-invalid` + `aria-describedby` im Block, Muster `Feld` in @kundeninfo/ui).
 */
export type ParteienGriff = {
  ref: { current: BelegParteienEntwurf | null };
  /** Hat jemand den Block angefasst? (Abbrechen-Rückfrage, `useBeforeUnload`.) */
  beruehrt: boolean;
  befunde: Readonly<Record<string, string>>;
  /** Der Griff für `onEntwurf` des Blocks. */
  melde: (entwurf: BelegParteienEntwurf) => void;
  setzeBefunde: (fehler: unknown) => void;
  /** Alles zurück — Panel neu aufgeschlagen, abgebrochen oder erfolgreich gespeichert. */
  zuruecksetzen: () => void;
};

export function useParteienGriff(): ParteienGriff {
  const ref = useRef<BelegParteienEntwurf | null>(null);
  const [beruehrt, setBeruehrt] = useState(false);
  const [befunde, setBefunde] = useState<Readonly<Record<string, string>>>({});
  return {
    ref, beruehrt, befunde,
    melde: (entwurf) => { ref.current = entwurf; setBeruehrt(true); },
    setzeBefunde: (fehler) => setBefunde(feldBefunde(fehler)),
    zuruecksetzen: () => { ref.current = null; setBeruehrt(false); setBefunde({}); },
  };
}

/** Die Feldbefunde eines Server-Fehlers als Satz je SERVERNAME — leer, wenn der Fehler
 *  keine trägt (Netzabbruch, 409, 500). Seit ME-Etappe 3 (Aufgabe 4) auch für die
 *  Anlegemasken (Befund am Feld). */
export function feldBefundeAus(fehler: unknown): Readonly<Record<string, string>> {
  return feldBefunde(fehler);
}

function feldBefunde(fehler: unknown): Readonly<Record<string, string>> {
  if (!(fehler instanceof ApiError) || fehler.fields === undefined) return {};
  const karte: Record<string, string> = {};
  for (const befund of fehler.fields) karte[befund.field] ??= befund.message;
  return karte;
}

/* ------------------------------------------- Die Naht zum Kopfblock ZAHLPLAN */

/**
 * Der DRITTE SPEICHERWEG eines Belegs — die Zahlungsvereinbarung (Teilprojekt 3).
 *
 * Bauart und Begründung wie bei `speichereParteien` darüber; drei Unterschiede, und
 * alle drei sind Absicht:
 *
 *  - **Er steht ZULETZT.** Kopf → Parteien → Zahlplan. Der Zahlplan ist der einzige
 *    Weg, der die GEPLANTEN FÄLLIGKEITEN neu rechnet — und die sollen auf dem fertigen
 *    Kopf stehen (Laufzeit, Periodenregel), nicht auf einem halben.
 *  - **Die `version` ist die aus der ANTWORT des vorigen Weges.** Jeder der drei Wege
 *    dreht die Belegversion weiter; mit der gelesenen liefe der dritte zwingend in eine
 *    409 über die eigene Änderung.
 *  - **Unberührt wird nichts geschrieben** (`griff.ref.current === null`): Sonst
 *    schriebe jedes Speichern des Kopfes auch den Zahlplan — und rechnete die
 *    Fälligkeiten neu, obwohl nur der Titel geändert werden sollte.
 */
export async function speichereZahlplan(auftrag: {
  client: Pick<ApiClient, "setZahlplan">;
  /** WORAN der Zahlplan hängt — seit Migration 029 auch eine ANGEBOTSVORLAGE
   *  (`art: "template"`); der Typ ist derselbe wie bei den Positionswegen. */
  bezug: Positionsbezug;
  /** Die Version aus der Antwort des VORIGEN Speicherweges. */
  version: number;
  griff: ZahlplanGriff;
  beiFehler: (meldung: string, fehler: unknown) => void;
  /** Die Version, die der Beleg NACH diesem Weg trägt. */
  beiErfolg?: (version: number) => void;
}): Promise<boolean> {
  const stand = auftrag.griff.ref.current;
  if (stand === null) return true;
  try {
    const antwort = await auftrag.client.setZahlplan(
      auftrag.bezug, { version: auftrag.version, raten: stand.raten });
    auftrag.griff.zuruecksetzen();
    auftrag.beiErfolg?.(antwort.version);
    return true;
  } catch (fehler) {
    auftrag.griff.setzeBefunde(fehler);
    auftrag.beiFehler(
      apiMeldung(fehler, "Die Zahlungsvereinbarung konnte nicht gespeichert werden."),
      fehler);
    return false;
  }
}

/** Der Griff der Maske auf den Entwurf ihres Zahlplan-Abschnitts — Bauart und
 *  Begründung wie `ParteienGriff` darüber (Ref für den Entwurf, Zustand für den
 *  Berührt-Schalter und die Feldbefunde). */
export type ZahlplanGriff = {
  ref: { current: ZahlplanEntwurf | null };
  beruehrt: boolean;
  befunde: Readonly<Record<string, string>>;
  melde: (entwurf: ZahlplanEntwurf) => void;
  setzeBefunde: (fehler: unknown) => void;
  zuruecksetzen: () => void;
};

export function useZahlplanGriff(): ZahlplanGriff {
  const ref = useRef<ZahlplanEntwurf | null>(null);
  const [beruehrt, setBeruehrt] = useState(false);
  const [befunde, setBefunde] = useState<Readonly<Record<string, string>>>({});
  return {
    ref, beruehrt, befunde,
    melde: (entwurf) => { ref.current = entwurf; setBeruehrt(true); },
    setzeBefunde: (fehler) => setBefunde(feldBefunde(fehler)),
    zuruecksetzen: () => { ref.current = null; setBeruehrt(false); setBefunde({}); },
  };
}
